import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addRoleAccess,
  listBranchModules,
  listRoleAccess,
  reviveRoleAccess,
  updateRoleAccess,
  withdrawRoleAccess,
} from '@/api/branchRelationships';
import { listDepartments } from '@/api/departments';
import { useAuth } from '@/auth/useAuth';
import {
  CoverageHint, CoveragePanel, ResizablePalette, SelectRow, type CoverageItem,
} from '@/components/common/allocationPanel';
import { AlertDialog } from '@/components/ui/AlertDialog';
import type { RoleAccess, RoleAccessWrite } from '@/types/oscar';
import { describeRoleScope, geographyLabel, yieldsNothing } from './schema';
import { ErrorBanner } from './shared';
import { inputCls } from './styles';

type View = 'module' | 'role';

/**
 * Role access — oscar.DepartmentModule, as an allocation board: the same shape
 * as Software and Hardware Allocation (and literally the same panel). Pick a
 * module and the right side splits roles into Allocated / Not allocated, or
 * pick a role and it splits the modules.
 *
 * ⛔ ALLOCATE GIVES THE GEOGRAPHIC RULE, NEVER COMPANY. Company is every branch
 * in the business, so it is its own click on the allocated row — the scope
 * pill — and a mis-click on Allocate can never widen a role that far.
 *
 * ⚠ A withdrawn pair is REVIVED by Allocate, not re-inserted: the pair is the
 * table's primary key and the server refuses a second insert.
 */
export function RoleAccessTab() {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const [view, setView] = useState<View>('module');
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [leftQuery, setLeftQuery] = useState('');
  const [withdrawing, setWithdrawing] = useState<RoleAccess | null>(null);
  const [error, setError] = useState<string | null>(null);

  const rolesQuery = useQuery({ queryKey: ['branch-rel', 'role-access'], queryFn: ({ signal }) => listRoleAccess(true, signal) });
  const modulesQuery = useQuery({ queryKey: ['branch-rel', 'modules'], queryFn: ({ signal }) => listBranchModules(signal) });
  const departmentsQuery = useQuery({
    queryKey: ['departments', { includeDeleted: false }],
    queryFn: ({ signal }) => listDepartments(false, signal),
  });

  const mappings = useMemo(() => rolesQuery.data ?? [], [rolesQuery.data]);
  const modules = useMemo(() => modulesQuery.data ?? [], [modulesQuery.data]);
  const departments = useMemo(() => departmentsQuery.data ?? [], [departmentsQuery.data]);

  /** Every pair, live or withdrawn, by `code|module`. */
  const byPair = useMemo(() => new Map(mappings.map((m) => [`${m.DepartmentCode}|${m.ModuleKey}`, m] as const)), [mappings]);
  const liveCount = (pred: (m: RoleAccess) => boolean) => mappings.filter((m) => !m.IsDeleted && pred(m)).length;

  /**
   * ⛔ THE UNION, NOT JUST THE DEPARTMENT LIST — the Hardware Allocation rule.
   * A live mapping can name a role that has since been retired; it still has to
   * be visible to be withdrawn.
   */
  const roles = useMemo(() => {
    const list = departments.map((d) => ({ code: d.DepartmentCode, desc: d.DepartmentDescription, geo: d.GeographicCode, retired: false }));
    for (const m of mappings) {
      if (!m.IsDeleted && !list.some((r) => r.code === m.DepartmentCode))
        list.push({ code: m.DepartmentCode, desc: m.DepartmentDescription, geo: m.GeographicCode, retired: true });
    }
    return list.sort((a, b) => a.code.localeCompare(b.code));
  }, [departments, mappings]);

  const onDone = () => { setError(null); void qc.invalidateQueries({ queryKey: ['branch-rel'] }); };
  const onFail = (e: unknown) => setError(e instanceof Error ? e.message : 'The change could not be saved.');

  const add = useMutation({ mutationFn: addRoleAccess, onSuccess: onDone, onError: onFail });
  const revive = useMutation({ mutationFn: reviveRoleAccess, onSuccess: onDone, onError: onFail });
  const update = useMutation({ mutationFn: updateRoleAccess, onSuccess: onDone, onError: onFail });
  const withdraw = useMutation({ mutationFn: withdrawRoleAccess, onSuccess: onDone, onError: onFail });
  const busy = add.isPending || revive.isPending || update.isPending || withdraw.isPending;

  const allocate = (code: string, moduleKey: string) => {
    const body: RoleAccessWrite = { DepartmentCode: code, ModuleKey: moduleKey, ScopeOverride: null };
    if (byPair.get(`${code}|${moduleKey}`)?.IsDeleted) revive.mutate(body);
    else add.mutate(body);
  };
  const toggleScope = (m: RoleAccess) =>
    update.mutate({ DepartmentCode: m.DepartmentCode, ModuleKey: m.ModuleKey, ScopeOverride: m.ScopeOverride === 'Company' ? null : 'Company' });

  // ── By module: which roles have the selected module ─────────────────────────
  const moduleRow = modules.find((m) => m.ModuleKey === selectedModule) ?? null;
  const moduleItems = useMemo<CoverageItem<RoleAccess>[]>(() => {
    if (!moduleRow) return [];
    return roles.map((r) => {
      const m = byPair.get(`${r.code}|${moduleRow.ModuleKey}`);
      return {
        key: r.code,
        code: r.code,
        label: r.desc,
        mono: true,
        badge: r.retired ? <RetiredBadge /> : m?.IsDeleted ? <WithdrawnBadge /> : <GeoBadge geo={r.geo} />,
        search: `${r.code} ${r.desc}`.toLowerCase(),
        alloc: m && !m.IsDeleted ? m : null,
        targetId: 0,
      };
    });
  }, [moduleRow, roles, byPair]);

  // ── By role: which modules the selected role has ────────────────────────────
  const roleRow = roles.find((r) => r.code === selectedRole) ?? null;
  const roleItems = useMemo<CoverageItem<RoleAccess>[]>(() => {
    if (!roleRow) return [];
    return modules.map((mod) => {
      const m = byPair.get(`${roleRow.code}|${mod.ModuleKey}`);
      return {
        key: mod.ModuleKey,
        code: mod.Title,
        label: mod.ModuleKey,
        mono: false,
        badge: m?.IsDeleted ? <WithdrawnBadge /> : undefined,
        search: `${mod.Title} ${mod.ModuleKey}`.toLowerCase(),
        alloc: m && !m.IsDeleted ? m : null,
        targetId: 0,
      };
    });
  }, [roleRow, modules, byPair]);

  const term = leftQuery.trim().toLowerCase();
  const visibleModules = modules.filter((m) => !term || `${m.Title} ${m.ModuleKey}`.toLowerCase().includes(term));
  const visibleRoles = roles.filter((r) => !term || `${r.code} ${r.desc}`.toLowerCase().includes(term));
  const loading = rolesQuery.isLoading || modulesQuery.isLoading || departmentsQuery.isLoading;

  const scopePill = (m: RoleAccess) => <ScopePill row={m} admin={isAdmin} busy={busy} onToggle={toggleScope} />;

  return (
    <div>
      <p className="text-sm text-darkGrey">
        A role gets a module for everyone who holds it. <strong>Geographic</strong> gives a branch role its
        location&rsquo;s branch and a region role every branch in its region; <strong>Company</strong> gives every
        branch. Allocate gives the geographic rule — click the pill to switch.
      </p>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {rolesQuery.isError && <ErrorBanner message={(rolesQuery.error as Error).message || 'Could not load role access.'} />}

      <div role="tablist" aria-label="Role access view" className="mb-3 mt-3 flex gap-2">
        {(['module', 'role'] as const).map((v) => (
          <button
            key={v}
            role="tab"
            type="button"
            aria-selected={view === v}
            onClick={() => { setView(v); setLeftQuery(''); }}
            className={`rounded-lg border px-3 py-1.5 text-sm font-semibold ${view === v ? 'border-navy bg-navy text-white' : 'border-border bg-white text-darkGrey'}`}
          >
            {v === 'module' ? 'By module' : 'By role'}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <div className="flex-1">
          <label className="mb-3 flex flex-col text-xs font-semibold text-darkGrey">
            {view === 'module' ? 'Search modules' : 'Search roles'}
            <input
              aria-label={view === 'module' ? 'Search modules' : 'Search roles'}
              value={leftQuery}
              onChange={(e) => setLeftQuery(e.target.value)}
              placeholder={view === 'module' ? 'Module name or key' : 'Code or description'}
              className={`${inputCls} mt-1 font-normal`}
            />
          </label>

          <ul className="space-y-2">
            {view === 'module'
              ? visibleModules.map((m) => (
                  <SelectRow
                    key={m.ModuleKey}
                    primary={m.Title}
                    secondary={m.ModuleKey}
                    count={liveCount((r) => r.ModuleKey === m.ModuleKey)}
                    countTone="bg-navy/10 text-navy"
                    selected={selectedModule === m.ModuleKey}
                    onSelect={() => setSelectedModule(m.ModuleKey)}
                  />
                ))
              : visibleRoles.map((r) => (
                  <SelectRow
                    key={r.code}
                    primary={r.code}
                    secondary={r.desc}
                    mono
                    badge={r.retired ? <RetiredBadge /> : <GeoBadge geo={r.geo} />}
                    count={liveCount((m) => m.DepartmentCode === r.code)}
                    countTone="bg-navy/10 text-navy"
                    selected={selectedRole === r.code}
                    onSelect={() => setSelectedRole(r.code)}
                  />
                ))}
            {loading && <li className="text-sm text-darkGrey/60">Loading…</li>}
            {!loading && view === 'module' && visibleModules.length === 0 && (
              <li className="text-sm text-darkGrey/60">No modules match.</li>
            )}
            {!loading && view === 'role' && visibleRoles.length === 0 && (
              <li className="text-sm text-darkGrey/60">No roles match.</li>
            )}
          </ul>
        </div>

        <ResizablePalette>
          {view === 'module' && (moduleRow
            ? (
              <CoveragePanel<RoleAccess>
                title={<>Roles with <span className="text-navy">{moduleRow.Title}</span></>}
                searchPlaceholder="Search roles"
                items={moduleItems}
                admin={isAdmin}
                busy={busy}
                renderAllocatedExtra={scopePill}
                onAllocate={(it) => allocate(it.code, moduleRow.ModuleKey)}
                onRemove={setWithdrawing}
              />
            )
            : <CoverageHint>Select a module on the left to see which roles have it — and which don’t.</CoverageHint>)}

          {view === 'role' && (roleRow
            ? (
              <CoveragePanel<RoleAccess>
                title={<>Modules for <span className="font-mono text-navy">{roleRow.code}</span></>}
                searchPlaceholder="Search modules"
                items={roleItems}
                admin={isAdmin}
                busy={busy}
                note={roleRow.geo === 'N' && (
                  <span className="text-orange">
                    A national role gets nothing on the geographic rule — switch it to Company, or use Manual access.
                  </span>
                )}
                renderAllocatedExtra={scopePill}
                onAllocate={(it) => allocate(roleRow.code, it.key)}
                onRemove={setWithdrawing}
              />
            )
            : <CoverageHint>Select a role on the left to see which modules it has — and which it doesn’t.</CoverageHint>)}
        </ResizablePalette>
      </div>

      <AlertDialog
        open={withdrawing !== null}
        onOpenChange={(o) => { if (!o) setWithdrawing(null); }}
        title="Withdraw this role access?"
        description={
          withdrawing
            ? `Everyone with role ${withdrawing.DepartmentCode} loses ${withdrawing.ModuleTitle} unless a manual row gives it to them. Allocate brings it back.`
            : undefined
        }
        confirmLabel="Withdraw"
        destructive
        onConfirm={() => {
          if (withdrawing) withdraw.mutate({ DepartmentCode: withdrawing.DepartmentCode, ModuleKey: withdrawing.ModuleKey });
        }}
      />
    </div>
  );
}

/**
 * The scope of an allocated role — Geographic or Company — and the switch
 * between them. Software Allocation's Mandatory pill, for scope.
 */
function ScopePill({ row, admin, busy, onToggle }: {
  row: RoleAccess; admin: boolean; busy: boolean; onToggle: (r: RoleAccess) => void;
}) {
  const company = row.ScopeOverride === 'Company';
  const nothing = yieldsNothing(row);
  return (
    <button
      type="button"
      disabled={!admin || busy}
      onClick={() => onToggle(row)}
      title={describeRoleScope(row.GeographicCode, row.ScopeOverride)}
      aria-label={`${row.DepartmentCode} has ${row.ModuleKey} at ${company ? 'Company' : 'Geographic'} scope — switch to ${company ? 'Geographic' : 'Company'}`}
      className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${
        company ? 'bg-orange/15 text-orange' : nothing ? 'bg-red/10 text-red' : 'bg-navy/10 text-navy'
      } ${admin ? 'hover:opacity-80' : 'cursor-default'}`}
    >
      {company ? 'Company' : nothing ? 'Geographic — gives nothing' : 'Geographic'}
    </button>
  );
}

function GeoBadge({ geo }: { geo: string }) {
  return (
    <span className="shrink-0 rounded-full bg-bg px-1.5 py-0.5 text-[10px] font-semibold text-darkGrey/70">
      {geographyLabel[geo] ?? geo}
    </span>
  );
}

function RetiredBadge() {
  return <span className="shrink-0 text-[10px] font-semibold text-orange">retired role</span>;
}

function WithdrawnBadge() {
  return <span className="shrink-0 text-[10px] text-darkGrey/60">withdrawn — Allocate revives it</span>;
}
